import React, { useEffect, useRef, useState } from 'react';
import { Button, Group, Notification, Paper, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { inject, observer } from 'mobx-react';
import * as z from 'zod';
import { t } from 'i18next';
import { DateTimePicker, DatesProvider } from '@mantine/dates';
import { useParams } from 'react-router';
import { IMaskInput } from 'react-imask';
import { useNavigate } from 'react-router-dom';

// Types and interfaces
import {
    IPhysiologicalDataDetails,
    IPhysiologicalType,
    IUnit,
} from '../../../global/entities/PhysiologicalData/PhysiologicalData';
import { AppMedError } from '../../../global/error/AppMedError';

// Hooks and providers
// Queries and mutations
import { useGetPhysiologicalDataById } from '../../../global/entities/PhysiologicalData/queries/useGetPhysiologicalDataById.tsx';
import { useGetPhysiologicalUnitsByType } from '../../../global/entities/PhysiologicalData/queries/useGetPhysiologicalUnitsByType.tsx';
import { useGetPhysiologicalTypes } from '../../../global/entities/PhysiologicalData/queries/useGetPhysiologicalTypes.tsx';
import { usePostPhysiologicalData } from '../../../global/entities/PhysiologicalData/mutations/usePostPhysiologicalDataMutations';
import { usePutPhysiologicalData } from '../../../global/entities/PhysiologicalData/mutations/usePutPhysiologicalDataMutations';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
import dayjs from 'dayjs';

// Physiological type IDs
const PHYSIOLOGICAL_TYPES = {
    WEIGHT: 1,
    BLOOD_PRESSURE: 2,
    HEART_RATE: 3,
    BODY_TEMPERATURE: 4,
    GLUCOSE: 5,
    RESTING_HEART_RATE: 10,
    SLEEPING_HEART_RATE: 11,
    HRV_RMSSD: 12,
    SLEEP: 13,
    SLEEP_EFFICIENCY: 14,
    AWAKENINGS: 15,
    NIGHTMARES: 16,
    STEPS: 17,
    ACTIVE_MINUTES: 18,
};

// Types whose value carries a decimal part. Their input accepts ',' or '.' and is normalised to
// '.' before submit, because Physiologicals.Value is stored as a string and the charts parseFloat()
// it — '7,5' would otherwise be plotted as 7.
const DECIMAL_TYPES: ReadonlySet<number> = new Set([
    PHYSIOLOGICAL_TYPES.BODY_TEMPERATURE,
    PHYSIOLOGICAL_TYPES.GLUCOSE,
    PHYSIOLOGICAL_TYPES.HRV_RMSSD,
    PHYSIOLOGICAL_TYPES.SLEEP,
    PHYSIOLOGICAL_TYPES.SLEEP_EFFICIENCY,
]);

interface IPhysiologicalFormProps {
    patientStore?: PatientStore;
    routeStore?: RouteStore;
}

interface IFormValues {
    id?: number;
    dateMeasured: Date;
    value: string;
    physiologicalTypeID: number;
    unitID: number;
    units: IUnit[];
    physiologicalTypes: IPhysiologicalType[];
    patientID: number;
    onlyShowInTypeSelection: boolean;
}

const requiredValue = () => z.string().min(1, { message: t('formError.valueIsRequired') });

const wholeNumber = (maxDigits: number, message: string) =>
    requiredValue().regex(new RegExp(`^\\d{1,${maxDigits}}$`), { message });

// A regex cannot express an upper bound, so the range check is a separate refinement.
const boundedDecimal = (maxDigits: number, max: number, message: string) =>
    requiredValue()
        .regex(new RegExp(`^\\d{1,${maxDigits}}([.,]\\d*)?$`), { message })
        .refine((value) => Number(value.replace(',', '.')) <= max, { message });

// Custom validation based on physiological type
const getValueValidation = (physiologicalTypeID: number) => {
    switch (physiologicalTypeID) {
        case PHYSIOLOGICAL_TYPES.BLOOD_PRESSURE:
            return z
                .string()
                .min(1, { message: t('formError.valueIsRequired') })
                .regex(/^\d{2,3}\s?\/\s?\d{1,3}$/, { message: t('formError.invalidBloodPressureFormat') });
        case PHYSIOLOGICAL_TYPES.HEART_RATE:
            return z
                .string()
                .min(1, { message: t('formError.valueIsRequired') })
                .regex(/^\d{1,3}$/, { message: t('formError.invalidHeartRateFormat') });
        case PHYSIOLOGICAL_TYPES.BODY_TEMPERATURE:
            return z
                .string()
                .min(1, { message: t('formError.valueIsRequired') })
                .regex(/^\d{1,2}([.,]\d*)?$/, { message: t('formError.invalidTemperatureFormat') });
        case PHYSIOLOGICAL_TYPES.GLUCOSE:
            return z
                .string()
                .min(1, { message: t('formError.valueIsRequired') })
                .regex(/^\d{1,3}([.,]\d*)?$/, { message: t('formError.invalidGlucoseFormat') });
        case PHYSIOLOGICAL_TYPES.RESTING_HEART_RATE:
        case PHYSIOLOGICAL_TYPES.SLEEPING_HEART_RATE:
            return wholeNumber(3, t('formError.invalidHeartRateFormat'));
        case PHYSIOLOGICAL_TYPES.AWAKENINGS:
        case PHYSIOLOGICAL_TYPES.NIGHTMARES:
            return wholeNumber(2, t('formError.invalidCountFormat'));
        case PHYSIOLOGICAL_TYPES.STEPS:
            return wholeNumber(6, t('formError.invalidCountFormat'));
        case PHYSIOLOGICAL_TYPES.ACTIVE_MINUTES:
            return wholeNumber(4, t('formError.invalidCountFormat'));
        case PHYSIOLOGICAL_TYPES.HRV_RMSSD:
            return boundedDecimal(4, 9999, t('formError.invalidHrvFormat'));
        case PHYSIOLOGICAL_TYPES.SLEEP:
            return boundedDecimal(2, 24, t('formError.invalidSleepFormat'));
        case PHYSIOLOGICAL_TYPES.SLEEP_EFFICIENCY:
            return boundedDecimal(3, 100, t('formError.invalidPercentFormat'));
        default:
            return z.string().min(1, { message: t('formError.valueIsRequired') });
    }
};

// Get input mask based on physiological type
const getInputMask = (physiologicalTypeID: number) => {
    switch (physiologicalTypeID) {
        case PHYSIOLOGICAL_TYPES.BLOOD_PRESSURE:
            return '000 / 000';
        case PHYSIOLOGICAL_TYPES.HEART_RATE:
            return '000';
        case PHYSIOLOGICAL_TYPES.BODY_TEMPERATURE:
            return '';
        case PHYSIOLOGICAL_TYPES.GLUCOSE:
            return '';
        default:
            return '';
    }
};

const PhysiologicalFormComponent: React.FC<IPhysiologicalFormProps> = ({ patientStore, routeStore }) => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const params = useParams<{ id: string }>();
    const initialLoadComplete = useRef(false);
    const navigate = useNavigate();

    // Form initialization
    const form = useForm<IFormValues>({
        mode: 'controlled',
        initialValues: {
            id: Number(params.id),
            dateMeasured: new Date(),
            value: '',
            physiologicalTypeID: 0,
            unitID: 0,
            units: [],
            physiologicalTypes: [],
            patientID: patientStore?.patientId ? Number(patientStore.patientId) : 0,
            onlyShowInTypeSelection: false,
        },
        validate: (values) => {
            const errors: Record<string, string | null> = {
                dateMeasured: null,
                physiologicalTypeID: z.number().min(1).safeParse(values.physiologicalTypeID).success
                    ? null
                    : t('formError.typeIsRequired'),
                unitID: z.number().min(1).safeParse(values.unitID).success ? null : t('formError.unitIsRequired'),
            };

            // Validate date
            try {
                const date = dayjs(values.dateMeasured).toDate();
                if (isNaN(date.getTime())) 
                    errors.dateMeasured = t('formError.dateIsRequired');
                
            } catch {
                errors.dateMeasured = t('formError.dateIsRequired');
            }

            // Only validate value if there's a physiological type selected
            if (values.physiologicalTypeID > 0) {
                const validation = getValueValidation(values.physiologicalTypeID).safeParse(values.value);
                if (!validation.success && validation.error.issues[0]) 
                    errors.value = validation.error.issues[0].message;
                 else 
                    errors.value = null;
                
            }

            return errors;
        },
    });

    // Data fetching hooks
    const { data: physiological } = useGetPhysiologicalDataById();
    const { data: units } = useGetPhysiologicalUnitsByType(form.values.physiologicalTypeID);
    const { data: physiologicalTypes } = useGetPhysiologicalTypes();
    const postPhysiologicalData = usePostPhysiologicalData();
    const putPhysiologicalData = usePutPhysiologicalData();

    // Counts (awakenings, nightmares, steps) are dimensionless. They still carry a UnitID because
    // Physiologicals.UnitID is NOT NULL, but that unit is deliberately blank-named, so offering a
    // "Unit" dropdown with an empty option would only confuse. The unitID is still submitted.
    const hasNamedUnit = (units || []).some((unit: IUnit) => (unit.name ?? '').trim() !== '');

    // Set default unit when units are loaded
    useEffect(() => {
        if (units && units.length > 0) 
            form.setFieldValue('unitID', units[0].id);
        
    }, [units]);

    // Scroll to top when form mounts
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Load existing physiological data into form
    useEffect(() => {
        if (!physiological) return;

        form.setValues({
            id: physiological.id,
            dateMeasured: new Date(new Date(physiological.dateMeasured).toISOString().slice(0, -1)),
            value: physiological.value,
            physiologicalTypeID: physiological.physiologicalTypeID,
            unitID: physiological.unitID,
            units: physiological.units,
            physiologicalTypes: physiological.physiologicalTypes,
            patientID: patientStore?.patientId ? Number(patientStore.patientId) : 0,
        });

        initialLoadComplete.current = true;
    }, [physiological, patientStore]);

    const handleSubmit = async (values: IPhysiologicalDataDetails) => {
        setError(null);
        setIsSubmitting(true);
        try {
            // Ensure decimals are formatted with period regardless of locale
            if (DECIMAL_TYPES.has(values.physiologicalTypeID)) {
                // Normalize decimal separator to period for backend
                values.value = values.value.replace(',', '.');
            }

            // Convert date to UTC
            const formattedDate = dayjs(values.dateMeasured).format('YYYY-MM-DDTHH:mm:ss.SSS');
            values.dateMeasured = new Date(formattedDate + 'Z');

            if (physiological) {
                await putPhysiologicalData.mutateAsync({
                    id: physiological.id.toString(),
                    data: values,
                });
            } else 
                await postPhysiologicalData.mutateAsync(values);
            
        } catch (e) {
            if (e instanceof AppMedError) 
                setError(e.message);
             else 
                setError('An unexpected error occurred.');
            
        } finally {
            setIsSubmitting(false);
            if (routeStore?.lastRoute) navigate(routeStore.lastRoute);
        }
    };

    const getValuePlaceholder = () => {
        switch (form.values.physiologicalTypeID) {
            case PHYSIOLOGICAL_TYPES.BLOOD_PRESSURE:
                return t('physiologicalForm.bloodPressurePlaceholder');
            case PHYSIOLOGICAL_TYPES.HEART_RATE:
                return t('physiologicalForm.heartRatePlaceholder');
            case PHYSIOLOGICAL_TYPES.BODY_TEMPERATURE:
                return t('physiologicalForm.temperaturePlaceholder');
            case PHYSIOLOGICAL_TYPES.GLUCOSE:
                return t('physiologicalForm.glucosePlaceholder');
            case PHYSIOLOGICAL_TYPES.RESTING_HEART_RATE:
                return t('physiologicalForm.restingHeartRatePlaceholder');
            case PHYSIOLOGICAL_TYPES.SLEEPING_HEART_RATE:
                return t('physiologicalForm.sleepingHeartRatePlaceholder');
            case PHYSIOLOGICAL_TYPES.HRV_RMSSD:
                return t('physiologicalForm.hrvPlaceholder');
            case PHYSIOLOGICAL_TYPES.SLEEP:
                return t('physiologicalForm.sleepPlaceholder');
            case PHYSIOLOGICAL_TYPES.SLEEP_EFFICIENCY:
                return t('physiologicalForm.sleepEfficiencyPlaceholder');
            case PHYSIOLOGICAL_TYPES.AWAKENINGS:
                return t('physiologicalForm.awakeningsPlaceholder');
            case PHYSIOLOGICAL_TYPES.NIGHTMARES:
                return t('physiologicalForm.nightmaresPlaceholder');
            case PHYSIOLOGICAL_TYPES.STEPS:
                return t('physiologicalForm.stepsPlaceholder');
            case PHYSIOLOGICAL_TYPES.ACTIVE_MINUTES:
                return t('physiologicalForm.activeMinutesPlaceholder');
            default:
                return t('physiologicalForm.value');
        }
    };

    return (
        <Paper p="md" shadow="xs" radius="md">
            <form onSubmit={form.onSubmit((values) => handleSubmit(values as IPhysiologicalDataDetails))}>
                <Stack w="100%" gap="md">
                    <Select
                        label={t('physiologicalForm.physiologicalType')}
                        placeholder={t('physiologicalForm.selectPhysiologicalType')}
                        data={(physiologicalTypes || [])
                            .filter((type, index, self) => 
                                index === self.findIndex(t => t.id === type.id) && !type.onlyShowInTypeSelection
                            )
                            .map((type: IPhysiologicalType) => ({
                                value: type.id.toString(),
                                label: type.name,
                            }))}
                        value={form.values.physiologicalTypeID ? form.values.physiologicalTypeID.toString() : null}
                        onChange={(value) => {
                            const numValue = value ? Number(value) : 0;
                            form.setFieldValue('physiologicalTypeID', numValue);
                            if (!value) 
                                form.setFieldValue('unitID', 0);
                            
                        }}
                        clearable
                        withAsterisk
                    />
                    {hasNamedUnit && (
                        <Select
                            label={t('physiologicalForm.unit')}
                            placeholder={t('physiologicalForm.selectUnit')}
                            data={(units || []).map((unit: IUnit) => ({
                                value: unit.id.toString(),
                                label: unit.name,
                            }))}
                            value={form.values.unitID ? form.values.unitID.toString() : null}
                            onChange={(value) => form.setFieldValue('unitID', value ? Number(value) : 0)}
                            clearable
                            disabled={!form.values.physiologicalTypeID}
                            withAsterisk
                        />
                    )}
                    <DatesProvider settings={{ locale: 'en-CA' }}>
                        <DateTimePicker
                            label={t('physiologicalForm.dateMeasured')}
                            placeholder="YYYY-MM-DD HH:mm"
                            valueFormat="YYYY-MM-DD HH:mm"
                            withSeconds={false}
                            clearable={false}
                            defaultValue={new Date()}
                            {...form.getInputProps('dateMeasured')}
                            withAsterisk 
                        />
                    </DatesProvider>

                    {form.values.physiologicalTypeID > 0 ? (
                        form.values.physiologicalTypeID === PHYSIOLOGICAL_TYPES.BLOOD_PRESSURE ? (
                            <TextInput
                                label={t('physiologicalForm.value')}
                                maxLength={20}
                                placeholder={getValuePlaceholder()}
                                withAsterisk
                                error={form.errors.value}
                                value={form.values.value}
                                onChange={(e) => form.setFieldValue('value', e.target.value)}
                                inputMode="numeric"
                            />
                        ) : DECIMAL_TYPES.has(form.values.physiologicalTypeID) ? (
                            <TextInput
                                label={t('physiologicalForm.value')}
                                maxLength={20}
                                placeholder={getValuePlaceholder()}
                                withAsterisk
                                error={form.errors.value}
                                value={form.values.value}
                                onChange={(e) => {
                                    // Allow both period and comma as decimal separators
                                    const input = e.target.value.replace(/[^\d.,]/g, '');

                                    // Ensure only one decimal separator exists
                                    const periodIndex = input.indexOf('.');
                                    const commaIndex = input.indexOf(',');

                                    if (periodIndex >= 0 && commaIndex >= 0) {
                                        // Both separators exist, keep the first one
                                        const firstSepIndex = Math.min(periodIndex, commaIndex);
                                        const firstSep = input[firstSepIndex];
                                        form.setFieldValue(
                                            'value',
                                            input.replace(/[.,]/g, '').substring(0, firstSepIndex) +
                                                firstSep +
                                                input.replace(/[.,]/g, '').substring(firstSepIndex),
                                        );
                                    } else 
                                        form.setFieldValue('value', input);
                                    
                                }}
                                inputMode="decimal"
                            />
                        ) : (
                            <TextInput
                                label={t('physiologicalForm.value')}
                                maxLength={20}
                                placeholder={getValuePlaceholder()}
                                withAsterisk
                                error={form.errors.value}
                                value={form.values.value}
                                onChange={(e) => form.setFieldValue('value', e.target.value)}
                                component={IMaskInput}
                                {...{
                                    mask: getInputMask(form.values.physiologicalTypeID),
                                    unmask: true,
                                    inputMode: 'numeric' as const,
                                }}
                            />
                        )
                    ) : (
                        <TextInput
                            label={t('physiologicalForm.value')}
                            placeholder={t('physiologicalForm.value')}
                            withAsterisk
                            disabled={true}
                            value=""
                        />
                    )}

                    <Group mt="md" justify="flex-end" w="100%">
                        <Button
                            disabled={isSubmitting}
                            type="button"
                            onClick={() => navigate(routeStore?.lastRoute || '..')}
                            variant="default"
                        >
                            {t('button.cancel')}
                        </Button>
                        <Button disabled={isSubmitting} type="submit">
                            {t('button.save')}
                        </Button>
                    </Group>
                </Stack>

                {error && (
                    <Notification mt="md" color="red" onClose={() => setError(null)}>
                        {error}
                    </Notification>
                )}
            </form>
        </Paper>
    );
};

const PhysiologicalForm = inject('patientStore', 'routeStore')(observer(PhysiologicalFormComponent));
export default PhysiologicalForm;
